import { useGetIdentity, useMenu } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { Card, Col, Row, Statistic, Typography } from "antd";
import { Link } from "react-router";

import { api } from "../api";
import type { Me } from "../session";

// Waiting work the staff member can act on (null: not their job), then shortcuts. More counts
// arrive with their screens (Milestone 12, steps 6–9).
type Waiting = {
  students: { total: number; withActivePlan: number } | null;
  verifiedTutors: number | null;
  lessons: { today: number; nextSevenDays: number } | null;
  pendingTutors: number | null;
  pendingReviews: number | null;
  pendingCancellations: number | null;
  /** Support threads waiting for the team's answer, read or not. */
  awaitingSupport: number | null;
  /** Reported conversations waiting for a decision. */
  openMessageReports: number | null;
  requestedPayouts: number | null;
  /** Approved tutors' profile changes waiting for approval (2026-10-13). */
  pendingProfileChanges: number | null;
  /** Approved tutors students cannot see a video of yet. */
  missingProfileVideo: number | null;
  /** Lessons of the last 7 days where a connection got poor, was lost or reconnected. */
  weakConnectionLessons: number | null;
};
export function DashboardPage() {
  const { data: me } = useGetIdentity<Me>();
  const { menuItems } = useMenu();
  const shortcuts = menuItems.filter((item) => item.name !== "dashboard");
  const { data: waiting } = useQuery({
    queryKey: ["office", "dashboard"],
    queryFn: () => api<Waiting>("/api/office/dashboard"),
  });

  return (
    <>
      <Typography.Title level={3}>Merhaba{me ? `, ${me.name}` : ""}</Typography.Title>
      {waiting && (waiting.students || waiting.verifiedTutors !== null || waiting.lessons) ? (
        <>
          <Typography.Paragraph type="secondary">Genel bakış</Typography.Paragraph>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {waiting.students ? (
              <>
                <Col xs={12} lg={4}>
                  <Card size="small">
                    <Statistic title="Öğrenci" value={waiting.students.total} />
                  </Card>
                </Col>
                <Col xs={12} lg={4}>
                  <Card size="small">
                    <Statistic
                      title="Aktif planlı öğrenci"
                      value={waiting.students.withActivePlan}
                    />
                  </Card>
                </Col>
              </>
            ) : null}
            {waiting.verifiedTutors !== null ? (
              <Col xs={12} lg={4}>
                <Card size="small">
                  <Statistic title="Onaylı eğitmen" value={waiting.verifiedTutors} />
                </Card>
              </Col>
            ) : null}
            {waiting.lessons ? (
              <>
                <Col xs={12} lg={4}>
                  <Card size="small">
                    <Statistic title="Bugünkü ders" value={waiting.lessons.today} />
                  </Card>
                </Col>
                <Col xs={12} lg={4}>
                  <Card size="small">
                    <Statistic title="Önümüzdeki 7 gün" value={waiting.lessons.nextSevenDays} />
                  </Card>
                </Col>
              </>
            ) : null}
          </Row>
        </>
      ) : null}
      {waiting &&
      [
        waiting.pendingTutors,
        waiting.pendingReviews,
        waiting.pendingCancellations,
        waiting.requestedPayouts,
        waiting.awaitingSupport,
        waiting.openMessageReports,
        waiting.pendingProfileChanges,
        waiting.missingProfileVideo,
        waiting.weakConnectionLessons,
      ].some((count) => count !== null) ? (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {waiting.pendingTutors !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/tutors">
                <Card hoverable>
                  <Statistic
                    title="İncelemede bekleyen eğitmen başvurusu"
                    value={waiting.pendingTutors}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.pendingProfileChanges !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/profile-changes">
                <Card hoverable>
                  <Statistic
                    title="Onay bekleyen profil değişikliği"
                    value={waiting.pendingProfileChanges}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.missingProfileVideo !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/tutors?video=missing">
                <Card hoverable>
                  <Statistic
                    title="Profil videosu eksik eğitmen"
                    value={waiting.missingProfileVideo}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.weakConnectionLessons !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/lessons?connection=problem">
                <Card hoverable>
                  <Statistic
                    title="Son 7 günde bağlantı sorunu olan ders"
                    value={waiting.weakConnectionLessons}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.pendingCancellations !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/cancellation-requests">
                <Card hoverable>
                  <Statistic
                    title="Bekleyen eğitmen iptal talebi"
                    value={waiting.pendingCancellations}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.requestedPayouts !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/payouts">
                <Card hoverable>
                  <Statistic title="Ödenecek ödeme talebi" value={waiting.requestedPayouts} />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.awaitingSupport !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/support">
                <Card hoverable>
                  <Statistic
                    title="Cevap bekleyen destek konuşması"
                    value={waiting.awaitingSupport}
                    valueStyle={waiting.awaitingSupport > 0 ? { color: "#cf1322" } : undefined}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.openMessageReports !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/message-reports">
                <Card hoverable>
                  <Statistic
                    title="Bekleyen sohbet bildirimi"
                    value={waiting.openMessageReports}
                    valueStyle={waiting.openMessageReports > 0 ? { color: "#cf1322" } : undefined}
                  />
                </Card>
              </Link>
            </Col>
          ) : null}
          {waiting.pendingReviews !== null ? (
            <Col xs={24} sm={12} lg={8}>
              <Link to="/reviews">
                <Card hoverable>
                  <Statistic title="Onay bekleyen yorum" value={waiting.pendingReviews} />
                </Card>
              </Link>
            </Col>
          ) : null}
        </Row>
      ) : null}
      <Typography.Paragraph type="secondary">Yetkin olan ekranlar:</Typography.Paragraph>
      <Row gutter={[16, 16]}>
        {shortcuts.map((item) => (
          <Col key={item.key} xs={24} sm={12} lg={8}>
            <Link to={item.route ?? "/"}>
              <Card hoverable>
                <Card.Meta avatar={item.icon} title={item.label} />
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </>
  );
}
